import type { DOMChangeDetectorOptions } from '../types';

export class DOMChangeDetector {
  private observer: MutationObserver | null = null;
  private options: DOMChangeDetectorOptions;
  private paused = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRecords: MutationRecord[] = [];
  private destroyed = false;

  constructor(options: DOMChangeDetectorOptions) {
    this.options = options;
    this.observer = new MutationObserver(records => this.handleMutations(records));
  }

  start(): void {
    if (this.destroyed || !this.observer) return;

    this.observer.observe(this.options.target, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: !!this.options.attributeFilter,
      attributeFilter: this.options.attributeFilter,
    });
  }

  stop(): void {
    this.observer?.disconnect();
    this.clearDebounce();
    this.pendingRecords = [];
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
  }

  destroy(): void {
    this.destroyed = true;
    this.stop();
    this.observer = null;
  }

  private handleMutations(records: MutationRecord[]): void {
    if (this.paused || this.destroyed) return;

    const filtered = this.filterRecords(records);
    if (filtered.length === 0) return;

    this.pendingRecords.push(...filtered);
    this.scheduleFlush();
  }

  private filterRecords(records: MutationRecord[]): MutationRecord[] {
    const ignoreSelector = this.options.ignoreSelector;
    if (!ignoreSelector) return records;

    return records.filter(record => {
      const target = record.target;
      if (target.nodeType === Node.ELEMENT_NODE) {
        const el = target as Element;
        if (el.matches(ignoreSelector) || el.closest(ignoreSelector)) {
          return false;
        }
      }

      if (target.nodeType === Node.TEXT_NODE && target.parentElement) {
        if (
          target.parentElement.matches(ignoreSelector) ||
          target.parentElement.closest(ignoreSelector)
        ) {
          return false;
        }
      }

      return true;
    });
  }

  private scheduleFlush(): void {
    this.clearDebounce();
    const ms = this.options.debounceMs ?? 300;
    this.debounceTimer = setTimeout(() => {
      this.flush();
    }, ms);
  }

  private flush(): void {
    const records = this.pendingRecords;
    this.pendingRecords = [];

    if (records.length > 0 && !this.destroyed) {
      this.options.onDetect(records);
    }
  }

  private clearDebounce(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}
